import { useEffect, useRef, useState } from 'react';

export const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

/**
 * Animates a headline number from its previous value up to `target` whenever
 * `target` changes. Jumps straight to the final value under reduced-motion.
 */
export default function useCountUp(target: number, durationMs = 600) {
    const [value, setValue] = useState(target);
    const fromRef = useRef(target);

    useEffect(() => {
        if (prefersReducedMotion()) {
            setValue(target);
            fromRef.current = target;
            return undefined;
        }

        const from = fromRef.current;
        const delta = target - from;
        if (delta === 0) return undefined;

        let frame: number;
        const start = performance.now();

        const tick = (now: number) => {
            const progress = Math.min((now - start) / durationMs, 1);
            setValue(from + delta * easeOutCubic(progress));
            if (progress < 1) {
                frame = requestAnimationFrame(tick);
            } else {
                fromRef.current = target;
            }
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, durationMs]);

    return Math.round(value);
}
